import { useMutation, useQuery } from '@tanstack/react-query';
import { post } from '@/lib/api/client';
import { dayBounds } from '@/lib/store-time';

/**
 * The inventory movement report.
 *
 * `inventory/history/report` is one page of rows plus the totals for the whole
 * filtered set; `inventory/history/report/all` is the same query with no paging
 * and **no totals** — a bare array, for building an export out of. Both take
 * the identical binding model, so the two calls cannot drift apart.
 */

const KEY = ['inventory-report'] as const;

export const PAGE_SIZE = 25;

/** `InventoryMovementReportSort`. */
export const REPORT_SORT = {
  default: 0,
  amountMovedAsc: 1,
  amountMovedDesc: 2,
  valueMovedAsc: 3,
  valueMovedDesc: 4,
  quantityOnHandAsc: 5,
  quantityOnHandDesc: 6,
  quantityOnHandValueAsc: 7,
  quantityOnHandValueDesc: 8,
} as const;

export interface ReportRow {
  gtin: string | null;
  name: string | null;
  nameSpa: string | null;
  totalQuantityOnHand: number;
  totalQuantityOnHandValue: number;
  movementAmount: number;
  movementValue: number;
}

export interface ReportResult {
  report: ReportRow[] | null;
  total: number;
  totalQuantityOnHand: number;
  totalQuantityOnHandValue: number;
  totalSale: number;
  totalAdjustmentUp: number;
  totalAdjustmentDown: number;
  totalReceiving: number;
  totalSaleAmount: number;
  totalAdjustmentUpAmount: number;
  totalAdjustmentDownAmount: number;
  totalReceivingAmount: number;
  totalSaleValue: number;
  totalAdjustmentUpValue: number;
  totalAdjustmentDownValue: number;
  totalReceivingValue: number;
}

export interface ReportQuery {
  query: string;
  businessId: string | null;
  type: number | null;
  reasons: number[];
  divisionFilter: string | null;
  sort: number;
  start: string;
  end: string;
}

function toBody(params: ReportQuery, page: number, size: number) {
  const { startDate, endDate } = dayBounds(params.start, params.end);
  return {
    Page: page,
    Size: size,
    Query: params.query.trim(),
    BusinessId: params.businessId,
    Type: params.type,
    // `Reason` (singular) is also on the model; the plural is the one the
    // report filters on, so only it is sent.
    Reasons: params.reasons,
    Sort: params.sort,
    StartDate: startDate,
    EndDate: endDate,
    DivisionFilter: params.divisionFilter,
  };
}

export function useInventoryReport(params: ReportQuery, page: number) {
  return useQuery({
    queryKey: [...KEY, params, page],
    queryFn: () =>
      post<ReportResult>('inventory/history/report', toBody(params, page, PAGE_SIZE)),
    staleTime: 60_000,
  });
}

/** Every matching row, for the export. Page and size are ignored server-side. */
export function useInventoryReportAll() {
  return useMutation({
    mutationFn: (params: ReportQuery) =>
      post<ReportRow[]>(
        'inventory/history/report/all',
        toBody(params, 0, PAGE_SIZE)
      ),
  });
}
